import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Button,
  Badge,
  Input,
  Alert,
  FormGroup,
  Label,
  UncontrolledCollapse
} from 'reactstrap';
import Select from 'react-select';
import { convertFromRaw } from 'draft-js';
import Swal from 'sweetalert2';
import { renderDraftContent } from '../../util/draftToHtml';
import { customDropdownWithBorder } from '../../util/reactCustomStyles';
import { fetchPassages, deletePassage } from '../../redux/QuestionBank/PassageAction';
import { fetchQuestions, deleteQuestion } from '../../redux/QuestionBank/QuestionsAction';
import { useDebounce } from '../../util/useDebounce';
import { formatDate } from '../../util/formatDate';

const PassagesList = ({ onEditPassage, onCreateQuestionForPassage, onEditQuestion }) => {
  const dispatch = useDispatch();

  // Redux state
  const { passages, loading } = useSelector(state => state.questionBankPassages);
  const { subjects } = useSelector(state => state.questionBankSubjects);
  const { units } = useSelector(state => state.questionBankUnits);
  const { topics } = useSelector(state => state.questionBankTopics);
  const { questions } = useSelector(state => state.questionBankQuestions);

  // Filter state
  const [filters, setFilters] = useState({
    subjectId: '',
    unitId: '',
    difficulty: '',
    quality: ''
  });

  // Sort state
  const [sortBy, setSortBy] = useState('newest');

  // Expanded passages state
  const [expandedPassages, setExpandedPassages] = useState({});

  // Expanded questions state (for viewing question details)
  const [expandedQuestions, setExpandedQuestions] = useState({});

  // Passage height state for resizable preview
  const [passageHeights, setPassageHeights] = useState({});

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const passagesPerPage = 5;

  // Search state
  const [passageSearch, setPassageSearch] = useState('');
  const debouncedPassageSearch = useDebounce(passageSearch, 300);

  useEffect(() => {
    dispatch(fetchPassages());
    dispatch(fetchQuestions());
  }, [dispatch]);

  const getQuestionsForPassage = (passageId) => {
    return questions.filter(q => q.passageId === passageId);
  };

  const filteredUnits = filters.subjectId
    ? units.filter(unit => unit.subjectId === filters.subjectId)
    : [];

  // M4: Reset pagination when filters or underlying data change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters.subjectId, filters.unitId, filters.difficulty, filters.quality, passages]);

  // Check if any filters are active
  const hasActiveFilters = filters.subjectId || filters.unitId || filters.difficulty || filters.quality;

  // Clear all filters
  const handleClearFilters = () => {
    setFilters({ subjectId: '', unitId: '', difficulty: '', quality: '' });
    setPassageSearch('');
    setSortBy('newest');
    setCurrentPage(1);
  };

  // Get questions for a passage
  const getPassageQuestions = (passageId) => {
    return questions.filter(q => q.passageId === passageId);
  };

  // Helper functions for getting names
  const getSubjectName = (subjectId) => {
    const subject = subjects.find(s => s.subjectId === subjectId);
    return subject ? subject.name : 'Unknown';
  };

  const getUnitName = (unitId) => {
    const unit = units.find(u => u.unitId === unitId);
    return unit ? unit.name : 'Unknown';
  };

  const getTopicName = (topicId) => {
    if (!topicId) return null;
    const topic = topics.find(t => t.topicId === topicId);
    return topic ? topic.name : null;
  };

  // Apply filters
  const filteredPassages = passages.filter(passage => {
    if (filters.subjectId && passage.subjectId !== filters.subjectId) return false;
    if (filters.unitId && passage.unitId !== filters.unitId) return false;
    const passageQuestions = getPassageQuestions(passage.passageId);
    if (filters.difficulty) {
      // Filter by difficulty - passage must have at least one question with matching difficulty
      const hasMatchingDifficulty = passageQuestions.some(q => q.difficulty === filters.difficulty);
      if (!hasMatchingDifficulty) return false;
    }
    if (filters.quality) {
      // Filter by quality - passage must have at least one question with matching quality
      const hasMatchingQuality = passageQuestions.some(q => q.quality === filters.quality);
      if (!hasMatchingQuality) return false;
    }
    return true;
  });

  // Apply search filter (debounced to avoid lag on every keystroke)
  const searchedPassages = React.useMemo(() => {
    if (!debouncedPassageSearch.trim()) return filteredPassages;
    const searchLower = debouncedPassageSearch.toLowerCase().trim();
    return filteredPassages.filter(passage => {
      // Search by passage title
      if (passage.title?.toLowerCase().includes(searchLower)) return true;
      // Search by passage text
      try {
        const contentState = convertFromRaw(passage.passageText);
        if (contentState.getPlainText().toLowerCase().includes(searchLower)) return true;
      } catch (e) {}
      // Search by subject
      if (getSubjectName(passage.subjectId)?.toLowerCase().includes(searchLower)) return true;
      // Search by unit
      if (getUnitName(passage.unitId)?.toLowerCase().includes(searchLower)) return true;
      // Search by tags
      if (passage.tags?.some(tag => tag.toLowerCase().includes(searchLower))) return true;
      // Search in questions
      const passageQuestions = getPassageQuestions(passage.passageId);
      const hasMatchingQuestion = passageQuestions.some(q => {
        if (q.questionCode?.toLowerCase().includes(searchLower)) return true;
        try {
          const qContentState = convertFromRaw(q.questionText);
          if (qContentState.getPlainText().toLowerCase().includes(searchLower)) return true;
        } catch (e) {}
        if (getTopicName(q.topicId)?.toLowerCase().includes(searchLower)) return true;
        if (q.difficulty?.toLowerCase().includes(searchLower)) return true;
        if (q.quality?.toLowerCase().includes(searchLower)) return true;
        if (q.tags?.some(tag => tag.toLowerCase().includes(searchLower))) return true;
        return false;
      });
      if (hasMatchingQuestion) return true;
      return false;
    });
  }, [filteredPassages, debouncedPassageSearch, questions, subjects, units, topics]);

  // Apply sorting
  const sortedPassages = React.useMemo(() => {
    const sorted = [...searchedPassages];
    switch (sortBy) {
      case 'newest':
        return sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      case 'oldest':
        return sorted.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      case 'title_asc':
        return sorted.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
      case 'title_desc':
        return sorted.sort((a, b) => (b.title || '').localeCompare(a.title || ''));
      case 'most_questions':
        return sorted.sort((a, b) => getPassageQuestions(b.passageId).length - getPassageQuestions(a.passageId).length);
      case 'least_questions':
        return sorted.sort((a, b) => getPassageQuestions(a.passageId).length - getPassageQuestions(b.passageId).length);
      default:
        return sorted;
    }
  }, [searchedPassages, sortBy, questions]);

  // Pagination
  const totalPages = Math.ceil(sortedPassages.length / passagesPerPage);
  const paginatedPassages = React.useMemo(() => {
    const startIndex = (currentPage - 1) * passagesPerPage;
    const endIndex = startIndex + passagesPerPage;
    return sortedPassages.slice(startIndex, endIndex);
  }, [sortedPassages, currentPage, passagesPerPage]);

  const expandAllPassages = () => {
    const allExpanded = {};
    paginatedPassages.forEach(passage => {
      allExpanded[passage.passageId] = true;
    });
    setExpandedPassages(prev => ({ ...prev, ...allExpanded }));
  };

  const collapseAllPassages = () => {
    const collapsed = { ...expandedPassages };
    paginatedPassages.forEach(passage => {
      delete collapsed[passage.passageId];
    });
    setExpandedPassages(collapsed);
  };

  const toggleQuestion = (questionId) => {
    setExpandedQuestions(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy': return 'success';
      case 'hard': return 'danger';
      default: return 'warning';
    }
  };

  const getQualityColor = (quality) => {
    return quality === 'excellent' ? 'success' : 'info';
  };

  const getChoiceLabel = (index) => {
    return String.fromCharCode(65 + index); // A, B, C, D...
  };

  const handleDeleteQuestion = async (questionId) => {
    const confirm = await Swal.fire({
      title: 'Archive Question?',
      text: 'This will archive the question and remove it from all question lists and test creation. Existing tests are not affected.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f5365c',
      cancelButtonColor: '#8898aa',
      confirmButtonText: 'Yes, archive it'
    });

    if (confirm.isConfirmed) {
      const result = await dispatch(deleteQuestion(questionId));
      if (result.success) {
        Swal.fire('Archived!', 'Question has been archived.', 'success');
        dispatch(fetchQuestions());
      } else {
        Swal.fire('Error', result.error, 'error');
      }
    }
  };

  const handlePassageResize = (passageId, e) => {
    e.preventDefault();
    const startY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
    const startHeight = passageHeights[passageId] || 200;

    const onMove = (moveEvent) => {
      const currentY = moveEvent.type === 'touchmove' ? moveEvent.touches[0].clientY : moveEvent.clientY;
      const newHeight = Math.max(100, Math.min(600, startHeight + (currentY - startY)));
      setPassageHeights(prev => ({ ...prev, [passageId]: newHeight }));
    };

    const onEnd = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onEnd);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onEnd);
    document.addEventListener('touchmove', onMove);
    document.addEventListener('touchend', onEnd);
  };

  // M10: Use shared helper (with M9 DOMPurify sanitization)
  const renderPassagePreview = (rawContent) => renderDraftContent(rawContent);

  const togglePassageExpansion = (passageId) => {
    setExpandedPassages(prev => ({
      ...prev,
      [passageId]: !prev[passageId]
    }));
  };

  const handleDelete = async (passage) => {
    const confirm = await Swal.fire({
      title: 'Delete Passage?',
      text: `Are you sure you want to delete "${passage.title}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f5365c',
      cancelButtonColor: '#8898aa',
      confirmButtonText: 'Yes, delete it!'
    });

    if (confirm.isConfirmed) {
      Swal.showLoading();
      const result = await dispatch(deletePassage(passage.passageId));
      Swal.close();

      if (result.success) {
        Swal.fire('Deleted!', 'Passage has been deleted.', 'success');
        dispatch(fetchPassages());
      } else {
        Swal.fire('Error', result.error, 'error');
      }
    }
  };

  return (
    <Card className="shadow mt-4">
      <CardHeader className="bg-transparent">
        <Row className="align-items-center">
          <Col>
            <h3 className="mb-0">Passages List</h3>
          </Col>
          <Col className="text-right">
            <Badge color="info" pill>
              {filteredPassages.length} Passage{filteredPassages.length !== 1 ? 's' : ''}
            </Badge>
          </Col>
        </Row>
      </CardHeader>
      <CardBody>
        {/* Filters */}
        <Row className="mb-2">
          <Col md="6" className="mb-2 mb-md-0">
            <FormGroup className="mb-0">
              <Label className="form-control-label">
                Filter by Subject
              </Label>
              <Select
                options={[
                  { value: '', label: 'All Subjects' },
                  ...subjects.map(subject => ({
                    value: subject.subjectId,
                    label: subject.name
                  }))
                ]}
                value={
                  filters.subjectId
                    ? { value: filters.subjectId, label: getSubjectName(filters.subjectId) }
                    : { value: '', label: 'All Subjects' }
                }
                onChange={(option) => setFilters({ ...filters, subjectId: option?.value || '', unitId: '' })}
                styles={customDropdownWithBorder}
                placeholder="All Subjects"
              />
            </FormGroup>
          </Col>
          <Col md="6" className="mb-2 mb-md-0">
            <FormGroup className="mb-0">
              <Label className="form-control-label">
                Filter by Unit
              </Label>
              <Select
                options={[
                  { value: '', label: 'All Units' },
                  ...filteredUnits.map(unit => ({
                    value: unit.unitId,
                    label: unit.name
                  }))
                ]}
                value={
                  filters.unitId
                    ? { value: filters.unitId, label: getUnitName(filters.unitId) }
                    : { value: '', label: 'All Units' }
                }
                onChange={(option) => setFilters({ ...filters, unitId: option?.value || '' })}
                styles={customDropdownWithBorder}
                placeholder="All Units"
                isDisabled={!filters.subjectId}
              />
            </FormGroup>
          </Col>
          {/* Difficulty filter hidden for now */}
        </Row>
        <Row className="mb-4">
          {/* Quality filter hidden for now
          <Col md="6" className="mb-2 mb-md-0">
            <FormGroup className="mb-0">
              <Label className="form-control-label">
                Filter by Quality
              </Label>
              <Select
                options={[
                  { value: '', label: 'All Qualities' },
                  { value: 'simple', label: 'Simple' },
                  { value: 'excellent', label: 'Excellent' }
                ]}
                value={
                  filters.quality
                    ? { value: filters.quality, label: filters.quality.charAt(0).toUpperCase() + filters.quality.slice(1) }
                    : { value: '', label: 'All Qualities' }
                }
                onChange={(option) => setFilters({ ...filters, quality: option?.value || '' })}
                styles={customDropdownWithBorder}
                placeholder="All Qualities"
              />
            </FormGroup>
          </Col>
          */}
          <Col md="6" className="mb-2 mb-md-0">
            <FormGroup className="mb-0">
              <Label className="form-control-label">
                Sort By
              </Label>
              <Select
                options={[
                  { value: 'newest', label: 'Newest First' },
                  { value: 'oldest', label: 'Oldest First' },
                  { value: 'title_asc', label: 'Title (A-Z)' },
                  { value: 'title_desc', label: 'Title (Z-A)' },
                  { value: 'most_questions', label: 'Most Questions' },
                  { value: 'least_questions', label: 'Least Questions' }
                ]}
                value={{ value: sortBy, label: {
                  'newest': 'Newest First',
                  'oldest': 'Oldest First',
                  'title_asc': 'Title (A-Z)',
                  'title_desc': 'Title (Z-A)',
                  'most_questions': 'Most Questions',
                  'least_questions': 'Least Questions'
                }[sortBy] }}
                onChange={(option) => setSortBy(option?.value || 'newest')}
                styles={customDropdownWithBorder}
              />
            </FormGroup>
          </Col>
        </Row>

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <div className="mb-3">
            <Button
              color="warning"
              size="sm"
              onClick={handleClearFilters}
            >
              <i className="fas fa-times mr-2"></i>
              Clear Filters
            </Button>
          </div>
        )}

        {/* Passages List */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        ) : passages.length === 0 ? (
          <Alert color="info">
            No passages found. Create your first passage above!
          </Alert>
        ) : filteredPassages.length === 0 ? (
          <Alert color="warning">
            <i className="fas fa-filter mr-2"></i>
            No passages match the current filters. Try adjusting your filters.
          </Alert>
        ) : (
          <>
            {/* Passages Header with Search and Controls */}
            <Row className="mb-2 align-items-center">
              <Col xs="12" md="6" className="mb-2 mb-md-0">
                <h4 className="mb-1">
                  <i className="fas fa-book-open mr-2 text-info"></i>
                  Manage Passages
                  <Badge color="info" pill className="ml-2">
                    {sortedPassages.length} passage{sortedPassages.length !== 1 ? 's' : ''}
                  </Badge>
                </h4>
                <small className="text-muted">
                  <i className="fas fa-lightbulb mr-1"></i>
                  Recommended: 5-7 questions per passage
                </small>
              </Col>
              <Col xs="12" md="6">
                <Input
                  type="text"
                  placeholder="Search by title, subject, unit, tag..."
                  value={passageSearch}
                  onChange={(e) => {
                    setPassageSearch(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </Col>
            </Row>
            <div className="d-flex justify-content-end mb-3 mt-2">
              <Button
                color="outline-primary"
                size="sm"
                onClick={expandAllPassages}
                className="mr-2"
              >
                <i className="fas fa-expand-alt mr-1"></i>
                Expand All
              </Button>
              <Button
                color="primary"
                size="sm"
                onClick={collapseAllPassages}
              >
                <i className="fas fa-compress-alt mr-1"></i>
                Collapse All
              </Button>
            </div>

            {passageSearch && searchedPassages.length === 0 ? (
              <Alert color="warning" className="mb-3">
                <i className="fas fa-search mr-2"></i>
                No passages found matching "{passageSearch}"
              </Alert>
            ) : paginatedPassages.map((passage) => {
                  const passageQuestions = getQuestionsForPassage(passage.passageId);
                  const isExpanded = expandedPassages[passage.passageId];

                  return (
                    <Card key={passage.passageId} className="mb-3">
                      <CardBody className="p-3">
                        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start">
                          <div className="flex-grow-1 mb-2 mb-sm-0">
                            <h5 className="mb-2" style={{ fontSize: '1.1rem' }}>
                              <i className="fas fa-book-open mr-2 text-info"></i>
                              {passage.title}
                            </h5>
                            <div className="mb-2 d-flex flex-wrap">
                              <Badge color="primary" className="mr-2 mb-1">{getSubjectName(passage.subjectId)}</Badge>
                              <Badge color="success" className="mr-2 mb-1">{getUnitName(passage.unitId)}</Badge>
                              {passageQuestions.length === 0 ? (
                                <Badge color="warning" className="mr-2 mb-1">
                                  <i className="fas fa-exclamation-triangle mr-1"></i>
                                  No questions
                                </Badge>
                              ) : (
                                <Badge color="info" className="mr-2 mb-1">
                                  {passageQuestions.length} question{passageQuestions.length !== 1 ? 's' : ''}
                                </Badge>
                              )}
                            </div>
                            <div className="text-muted small mb-2">
                              {passage.createdAt && (
                                <span className="mr-3">
                                  <i className="fas fa-calendar-plus mr-1"></i>
                                  Created: {formatDate(passage.createdAt)}
                                </span>
                              )}
                              {passage.updatedAt && (
                                <span>
                                  <i className="fas fa-calendar-check mr-1"></i>
                                  Updated: {formatDate(passage.updatedAt)}
                                </span>
                              )}
                            </div>
                            {!isExpanded && (
                              <div className="text-muted small d-none d-sm-block">
                                <i className="fas fa-info-circle mr-1"></i>
                                Click "View Details" to see passage text and questions
                              </div>
                            )}
                          </div>
                          <div className="d-flex flex-row align-items-start">
                            <Button
                              color="primary"
                              size="sm"
                              onClick={() => togglePassageExpansion(passage.passageId)}
                              outline={!isExpanded}
                              className="mr-2"
                            >
                              <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} mr-1`}></i>
                              {isExpanded ? 'Hide' : 'View'}
                              <span className="d-none d-sm-inline"> Details</span>
                            </Button>
                            <Button
                              color="info"
                              size="sm"
                              className="mr-2"
                              onClick={() => onEditPassage(passage)}
                              title="Edit passage"
                            >
                              <i className="fas fa-edit"></i>
                            </Button>
                            <Button
                              color="danger"
                              size="sm"
                              onClick={() => handleDelete(passage)}
                              title="Delete passage"
                            >
                              <i className="fas fa-trash"></i>
                            </Button>
                          </div>
                        </div>

                        {isExpanded && (
                          <>
                            <hr />
                            <div className="border rounded mb-3">
                              <div className="p-3" style={{ height: `${passageHeights[passage.passageId] || 200}px`, overflow: 'auto' }}>
                                {renderPassagePreview(passage.passageText)}
                              </div>
                              <div
                                className="d-flex align-items-center justify-content-center border-top"
                                style={{
                                  backgroundColor: '#e9ecef',
                                  padding: '8px 0',
                                  cursor: 'ns-resize',
                                  touchAction: 'none'
                                }}
                                onMouseDown={(e) => handlePassageResize(passage.passageId, e)}
                                onTouchStart={(e) => handlePassageResize(passage.passageId, e)}
                              >
                                <div
                                  style={{
                                    width: '60px',
                                    height: '6px',
                                    backgroundColor: '#adb5bd',
                                    borderRadius: '3px'
                                  }}
                                />
                              </div>
                            </div>

                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <h6 className="mb-0 text-muted font-weight-bold">Questions for this passage:</h6>
                              {onCreateQuestionForPassage && (
                                <Button
                                  color="success"
                                  size="sm"
                                  onClick={() => onCreateQuestionForPassage(passage)}
                                >
                                  <i className="fas fa-plus mr-1"></i>
                                  Add Question
                                </Button>
                              )}
                            </div>

                      {passageQuestions.length === 0 ? (
                        <Alert color="warning" className="mb-0">
                          <i className="fas fa-exclamation-triangle mr-2"></i>
                          No questions linked to this passage yet. Add 5-7 questions for this passage.
                        </Alert>
                      ) : (
                        <div className="questions-list">
                          {passageQuestions.map((question, qIndex) => (
                            <Card key={question.questionId} className="mb-2 border">
                              <CardBody className="p-3">
                                <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start">
                                  <div className="flex-grow-1 mb-2 mb-sm-0">
                                    <div className="mb-2 d-flex flex-wrap">
                                      <Badge color="danger" className="mr-2 mb-1">Q{qIndex + 1}</Badge>
                                      {question.questionCode && (
                                        <Badge color="primary" className="mr-2 mb-1">{question.questionCode}</Badge>
                                      )}
                                      {getTopicName(question.topicId) && (
                                        <Badge color="primary" pill className="mr-2 mb-1">
                                          {getTopicName(question.topicId)}
                                        </Badge>
                                      )}
                                      {/* Difficulty badge hidden for now */}
                                      {/* Quality badge hidden for now
                                      <Badge color={getQualityColor(question.quality)} pill className="mr-2 mb-1">
                                        {question.quality}
                                      </Badge>
                                      */}
                                      <span className="text-muted small">{question.choices?.length || 0} choices</span>
                                    </div>
                                    <div style={{ maxHeight: '60px', overflow: 'hidden' }}>
                                      {renderPassagePreview(question.questionText)}
                                    </div>
                                    <div className="text-muted small mt-2">
                                      {question.createdAt && (
                                        <span className="mr-3">
                                          <i className="fas fa-calendar-plus mr-1"></i>
                                          Created: {formatDate(question.createdAt)}
                                        </span>
                                      )}
                                      {question.updatedAt && (
                                        <span>
                                          <i className="fas fa-calendar-check mr-1"></i>
                                          Updated: {formatDate(question.updatedAt)}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="d-flex flex-row flex-sm-column flex-md-row">
                                    <Button
                                      color="primary"
                                      size="sm"
                                      id={`toggler-${question.questionId}`}
                                      onClick={() => toggleQuestion(question.questionId)}
                                      title={expandedQuestions[question.questionId] ? "Collapse" : "View Details"}
                                      className="mr-2 mb-1"
                                    >
                                      <i className={`fas fa-eye${expandedQuestions[question.questionId] ? '-slash' : ''}`}></i>
                                    </Button>
                                    {onEditQuestion && (
                                      <Button
                                        color="info"
                                        size="sm"
                                        onClick={() => onEditQuestion(question)}
                                        title="Edit"
                                        className="mr-2 mb-1"
                                      >
                                        <i className="fas fa-edit"></i>
                                      </Button>
                                    )}
                                    <Button
                                      color="warning"
                                      size="sm"
                                      onClick={() => handleDeleteQuestion(question.questionId)}
                                      title="Archive"
                                      className="mb-1"
                                    >
                                      <i className="fas fa-archive"></i>
                                    </Button>
                                  </div>
                                </div>

                                <UncontrolledCollapse toggler={`#toggler-${question.questionId}`}>
                                  <hr className="my-3" />
                                  <div>
                                    <h6 className="text-uppercase text-muted font-weight-bold mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                                      <i className="fas fa-question-circle mr-2"></i>Full Question
                                    </h6>
                                    {renderPassagePreview(question.questionText)}

                                    <h6 className="text-uppercase text-muted font-weight-bold mt-4 mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                                      <i className="fas fa-list-ul mr-2"></i>Choices
                                    </h6>
                                    {question.choices?.map((choice, idx) => (
                                      <div key={idx} className="mb-2 p-3 border rounded" style={{ backgroundColor: question.correctAnswer?.includes(choice.choiceId) ? '#d4edda' : '#f8f9fa', display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                                        <div style={{ marginRight: '12px', flexShrink: 0 }}>
                                          <Badge
                                            color={question.correctAnswer?.includes(choice.choiceId) ? 'success' : 'secondary'}
                                            pill
                                            style={{
                                              minWidth: '32px',
                                              height: '32px',
                                              fontSize: '0.9rem',
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              padding: '0'
                                            }}
                                          >
                                            {question.correctAnswer?.includes(choice.choiceId) ? '✓' : getChoiceLabel(idx)}
                                          </Badge>
                                        </div>
                                        <div style={{ flex: 1, wordBreak: 'break-word', minWidth: 0 }}>
                                          {renderPassagePreview(choice.text)}
                                        </div>
                                      </div>
                                    ))}

                                    <h6 className="text-uppercase text-muted font-weight-bold mt-4 mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                                      <i className="fas fa-lightbulb mr-2"></i>Explanation
                                    </h6>
                                    {renderPassagePreview(question.explanationText)}

                                    {question.tags && question.tags.length > 0 && (
                                      <>
                                        <h6 className="text-uppercase text-muted font-weight-bold mt-4 mb-3" style={{ fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                                          <i className="fas fa-tags mr-2"></i>Tags
                                        </h6>
                                        {question.tags.map((tag, idx) => (
                                          <Badge key={idx} color="info" className="mr-2">
                                            {tag}
                                          </Badge>
                                        ))}
                                      </>
                                    )}
                                  </div>
                                </UncontrolledCollapse>
                              </CardBody>
                            </Card>
                          ))}
                          {/* {passageQuestions.length < 5 && (
                            <Alert color="info" className="mt-2 mb-0">
                              <i className="fas fa-info-circle mr-2"></i>
                              Recommended: Add {5 - passageQuestions.length} more question(s) to reach the minimum of 5 questions per passage.
                            </Alert>
                          )} */}
                        </div>
                      )}
                          </>
                        )}
                      </CardBody>
                    </Card>
                  );
                })}

            {/* Pagination Controls */}
            {searchedPassages.length > 0 && (
              <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center mt-3">
                <div className="text-muted small mb-2 mb-sm-0">
                  Showing {((currentPage - 1) * passagesPerPage) + 1} to {Math.min(currentPage * passagesPerPage, searchedPassages.length)} of {searchedPassages.length} passages
                </div>
                <div className="d-flex align-items-center">
                  <Button
                    color="primary"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1 || totalPages <= 1}
                    className="mr-2"
                  >
                    <i className="fas fa-chevron-left"></i>
                    <span className="d-none d-sm-inline ml-1">Previous</span>
                  </Button>
                  <span className="mx-2 text-nowrap">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    color="primary"
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages || totalPages <= 1}
                    className="ml-2"
                  >
                    <span className="d-none d-sm-inline mr-1">Next</span>
                    <i className="fas fa-chevron-right"></i>
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
};

export default PassagesList;
